import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, MonitorPlay, ShieldAlert, Shield, RefreshCw } from 'lucide-react';
import EtatGeneral from '../components/EtatGeneral';
import DashboardStats from '../components/dashboard/DashboardStats';
import SituationMapCard from '../components/dashboard/SituationMapCard';
import DashboardFullscreenMap from '../components/dashboard/DashboardFullscreenMap';
import { EngagedPersonnelSection, CrisisDirectoryCard } from '../components/dashboard/PersonnelWidgets';
import useLiveRefresh from '../hooks/useLiveRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
    alerte: 'bg-red-500', decision: 'bg-amber-500', intervention: 'bg-blue-500',
    logistique: 'bg-purple-500', communication: 'bg-green-500', general: 'bg-zinc-500'
};
const catLabels = {
    alerte: 'Alerte', decision: 'Decision', intervention: 'Intervention',
    logistique: 'Logistique', communication: 'Communication', general: 'General'
};

const ViewerMainCourante = ({ mainCourante }) => (
    <Card className="content-card" data-testid="viewer-main-courante">
        <CardHeader className="card-header">
            <CardTitle className="card-title flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-cyan-500" /> Main courante
            </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
            <div className="max-h-[360px] overflow-y-auto">
                {mainCourante.length > 0 ? (
                    <div className="relative pl-6">
                        <div className="absolute left-[11px] top-0 bottom-0 w-px bg-zinc-700"></div>
                        {[...mainCourante].reverse().map((evt) => (
                            <div key={evt.id} className="relative py-3 pr-4">
                                <div className={`absolute left-[-13px] top-4 w-2.5 h-2.5 rounded-full ${catColors[evt.category] || catColors.general} ring-2 ring-zinc-900`}></div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="font-mono text-xs text-cyan-400">
                                        {new Date(evt.event_time).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${catColors[evt.category] || catColors.general} bg-opacity-20 text-white`}>
                                        {catLabels[evt.category] || evt.category}
                                    </span>
                                </div>
                                <p className="text-sm text-white">{evt.description}</p>
                                <p className="text-[10px] text-zinc-600 mt-0.5">par {evt.created_by}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 text-center text-zinc-500">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun evenement enregistre</p>
                    </div>
                )}
            </div>
        </CardContent>
    </Card>
);

const ViewerHeader = ({ stats, etatGeneral, lastRefresh }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
        const tick = setInterval(() => {
            setCurrentTime(new Date());
            if (stats?.pcs_activation_time && stats?.event_active !== false) {
                const diff = Math.max(0, new Date() - new Date(stats.pcs_activation_time));
                const h = Math.floor(diff / 3600000);
                const m = Math.floor((diff % 3600000) / 60000);
                const s = Math.floor((diff % 60000) / 1000);
                setElapsed(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
            }
        }, 1000);
        return () => clearInterval(tick);
    }, [stats?.pcs_activation_time, stats?.event_active]);

    return (
        <div className="page-header">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <div className="glass-panel px-5 py-3 rounded-lg" data-testid="viewer-current-time">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Heure actuelle</p>
                        <p className="font-mono text-4xl text-white font-bold leading-tight">
                            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                        <p className="text-xs text-zinc-500 mt-0.5">{currentTime.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                    {stats?.pcs_activation_time && stats?.event_active !== false && (
                        <div className="glass-panel px-5 py-3 rounded-lg border border-red-500/30" data-testid="viewer-pcs-chrono">
                            <p className="text-[10px] text-red-400 uppercase tracking-wider flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span> PCS actif
                            </p>
                            <p className="font-mono text-4xl text-red-400 font-bold leading-tight">{elapsed || '00:00:00'}</p>
                            <p className="text-xs text-zinc-500 mt-0.5">depuis {new Date(stats.pcs_activation_time).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                    )}
                </div>
                <div className="flex-1 flex justify-center">
                    <EtatGeneral externalData={etatGeneral} />
                </div>
                <div className="glass-panel px-4 py-3 rounded-lg border border-cyan-500/30 text-right" data-testid="viewer-badge">
                    <p className="text-[10px] text-cyan-400 uppercase tracking-wider font-semibold flex items-center justify-end gap-1.5">
                        <MonitorPlay className="w-3.5 h-3.5" /> Mode Viewer
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">Lecture seule</p>
                    {lastRefresh && (
                        <p className="text-[10px] text-zinc-600 mt-0.5 flex items-center justify-end gap-1">
                            <RefreshCw className="w-2.5 h-2.5" /> MAJ {lastRefresh.toLocaleTimeString('fr-FR')}
                        </p>
                    )}
                </div>
            </div>
            {stats?.event_active === false && (
                <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 font-medium">Evenement PCS cloture</span>
                </div>
            )}
        </div>
    );
};

export default function ViewerPage() {
    const { shareToken } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [fsMapOpen, setFsMapOpen] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/viewer/public/${shareToken}`);
            setData(res.data);
            setError(false);
            setLastRefresh(new Date());
        } catch (err) {
            if (err.response?.status === 403) setError(true);
        } finally {
            setLoading(false);
        }
    }, [shareToken]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    useLiveRefresh(fetchData);

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="glass-panel p-8 rounded-xl border border-red-500/30 text-center max-w-md" data-testid="viewer-invalid-link">
                    <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Lien invalide ou révoqué</h1>
                    <p className="text-sm text-zinc-400">
                        Ce lien de partage n'est plus actif. Contactez le poste de commandement pour obtenir un nouveau lien.
                    </p>
                </div>
            </div>
        );
    }

    const { stats, etat_general, incidents, care_centers, vehicles, danger_zones, main_courante, directory, engaged } = data;
    const mapCenter = incidents.length > 0
        ? [incidents[0].latitude, incidents[0].longitude]
        : care_centers.find(c => c.latitude)
        ? [care_centers.find(c => c.latitude).latitude, care_centers.find(c => c.latitude).longitude]
        : [46.603354, 1.888334];

    return (
        <div className="min-h-screen bg-background p-4 md:p-6">
            <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="viewer-page">
                <ViewerHeader stats={stats} etatGeneral={etat_general} lastRefresh={lastRefresh} />

                <DashboardStats stats={stats} onShowShifts={() => {}} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <SituationMapCard
                        theme="dark" mapCenter={mapCenter}
                        incidents={incidents} careCenters={care_centers} vehicles={vehicles} dangerZones={danger_zones} drawings={data.drawings || []}
                        onFullscreen={() => setFsMapOpen(true)}
                    />
                    <ViewerMainCourante mainCourante={main_courante} />
                </div>

                {engaged.length > 0 && <EngagedPersonnelSection engaged={engaged} />}

                {directory.length > 0 && <CrisisDirectoryCard directory={directory} />}

                <DashboardFullscreenMap
                    open={fsMapOpen} onClose={() => setFsMapOpen(false)} mapCenter={mapCenter} theme="dark" drawings={data.drawings || []}
                    incidents={incidents} careCenters={care_centers} vehicles={vehicles} dangerZones={danger_zones}
                />

                <p className="text-center text-[11px] text-zinc-600 pb-4">
                    CIVIS+ — Dashboard partagé en lecture seule · Actualisation automatique toutes les 30 secondes
                </p>
            </div>
        </div>
    );
}
